/**
 * Друкована форма ПН / РК за ОФІЦІЙНИМ бланком №1307 (у ред. наказу №400)
 * — 2026-08-05, рішення власника (звірено з живими PDF з кабінету ДПС).
 *
 * Вихідна ПН/РК: дані з /print-data (рядки РК — ТЕ САМЕ ядро, що XML:
 * сторно/залишок, коди 103/102, групи, знаки — Закон #6). Вхідна ПН —
 * бланк ПН з рядків документа (копія накладної постачальника).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  taxInvoicesIncoming,
  type TaxInvoiceIncoming,
} from '@/api/taxInvoices';
import { api } from '@/api/client';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import SaveXlsxButton from '@/shared/components/SaveXlsxButton';

const fmt2 = (v: number | string | null | undefined) =>
  v == null || v === '' ? '-' : Number(v).toLocaleString('uk-UA',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQ = (v: number | string | null | undefined) =>
  v == null || v === '' ? '' : Number(v).toLocaleString('uk-UA',
    { maximumFractionDigits: 4 });
const dmy = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${d.getFullYear()}`;
};

interface PrintHeader {
  is_correction: boolean;
  invoice_number: string;
  invoice_date: string;
  not_issued: boolean;
  not_issued_reason: string | null;
  seller: { name: string; ipn: string; tin: string };
  buyer: { name: string; ipn: string; tin: string };
  director: { name: string | null; inn: string };
  corrected: { invoice_number: string; invoice_date: string } | null;
}
interface RkRow {
  g001: string; g21: string; g22: string; g3s: string; g4: string | null;
  g4s: string | null; g105: string | null; g008: string;
  g5: string; g6: string; g010: string; g11: string;
}
interface PnRow {
  n: number; descr: string; ukt: string | null; uom_sign: string | null;
  uom_code: string | null; qty: string; price: string; stavka: string;
  base: string; vat: string;
}
interface PrintData {
  kind: 'PN' | 'RK';
  header: PrintHeader;
  rows: (RkRow | PnRow)[];
  totals?: Record<string, string>;
}

const B = '1px solid #000';
const cell: React.CSSProperties = { border: B, padding: '1px 4px',
  verticalAlign: 'middle' };
const cellC: React.CSSProperties = { ...cell, textAlign: 'center' };
const cellR: React.CSSProperties = { ...cell, textAlign: 'right' };
const sub: React.CSSProperties = { fontSize: '6pt', color: '#333',
  textAlign: 'center' };

/** Рамка-значення (порожня, якщо нема). */
function Box({ v, w }: { v?: string | null; w?: number }) {
  return (
    <span style={{ display: 'inline-block', minWidth: w ?? 90,
      border: B, padding: '1px 6px', textAlign: 'center', fontWeight: 600,
      minHeight: '1.1em', verticalAlign: 'middle' }}>
      {v || ' '}
    </span>
  );
}

/** Верхній лівий блок відміток (спільний ПН/РК). */
function TopMarks({ h }: { h: PrintHeader }) {
  const row = (label: string, mark?: string | null) => (
    <tr>
      <td style={{ ...cell, fontWeight: 600 }}>{label}</td>
      <td style={{ ...cellC, width: 40 }}>{mark || ' '}</td>
    </tr>
  );
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <tbody>
        {row('Зведена податкова накладна')}
        {row('Складена на операції, звільнені від оподаткування')}
        <tr>
          <td style={{ ...cell, fontWeight: 600 }}>
            Не підлягає наданню отримувачу (покупцю)
            <div style={{ fontWeight: 400, fontSize: '7pt' }}>
              з причини (зазначається відповідний тип причини)
            </div>
          </td>
          <td style={{ ...cellC, width: 40 }}>
            {h.not_issued ? (h.not_issued_reason || 'X') : ' '}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function ExportInfo() {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ textAlign: 'center', fontSize: '8pt', fontWeight: 600 }}>
        Інформація про операцію з вивезення товарів за межі митної території України:
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 2 }}>
        <tbody>
          <tr>
            <td style={{ width: 90, fontSize: '8pt' }}>Договір (контракт)</td>
            <td style={{ ...cellC, width: 90 }}><div style={sub}>(дата)</div></td>
            <td style={cellC}><div style={sub}>(номер)</div></td>
            <td style={{ ...cellC, width: 160 }}>
              <div style={sub}>(відмітка про форвардний контракт)</div>
            </td>
          </tr>
          <tr>
            <td style={{ fontSize: '8pt' }}>Митна декларація</td>
            <td style={cellC}><div style={sub}>(дата)</div></td>
            <td style={cellC} colSpan={2}><div style={sub}>(номер)</div></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Блок сторони (постачальник/отримувач). */
function Party({ title, p }: { title: string;
  p: { name: string; ipn: string; tin: string } }) {
  return (
    <div style={{ border: B, padding: '2px 6px' }}>
      <div style={{ fontWeight: 600, fontSize: '8pt' }}>{title}</div>
      <div style={{ textAlign: 'center', fontWeight: 700, minHeight: '1.2em' }}>
        {p.name}
      </div>
      <div style={{ ...sub }}>
        (найменування; прізвище (за наявності), ім'я, по батькові (за наявності) —
        для фізичної особи — підприємця)
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 2 }}>
        <tbody>
          <tr>
            <td style={{ ...cellC, width: '40%', fontWeight: 700 }}>{p.ipn || '—'}</td>
            <td style={{ ...cellC, width: '15%' }}>{' '}</td>
            <td style={{ ...cellC, width: '30%', fontWeight: 700 }}>{p.tin || ' '}</td>
            <td style={{ ...cellC, width: '15%' }}>{p.tin ? '1' : ' '}</td>
          </tr>
          <tr style={{ fontSize: '6pt', color: '#333' }}>
            <td style={{ textAlign: 'center' }}>(індивідуальний податковий номер)</td>
            <td style={{ textAlign: 'center' }}>(номер філії²)</td>
            <td style={{ textAlign: 'center' }}>
              (податковий номер платника податку³ або серія (за наявності) та
              номер паспорта⁴)
            </td>
            <td style={{ textAlign: 'center' }}>(код⁵)</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function TaxInvoicePrintPage({ kind }: { kind: 'outgoing' | 'incoming' }) {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  // Вихідна: офіційні дані друку з бекенда (ядро XML — Закон #6).
  const printQ = useQuery<PrintData>({
    queryKey: ['tax-invoice-print-data', id],
    queryFn: () => api.get<PrintData>(`/tax-invoices-outgoing/${id}/print-data`)
      .then((r) => r.data),
    enabled: kind === 'outgoing' && !!id,
  });
  // Вхідна: рядки документа (копія накладної постачальника).
  const incQ = useQuery<TaxInvoiceIncoming>({
    queryKey: ['tax-invoice-incoming', id],
    queryFn: () => taxInvoicesIncoming.get(id!),
    enabled: kind === 'incoming' && !!id,
  });
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });

  let data: PrintData | null = null;
  if (kind === 'outgoing') {
    data = printQ.data ?? null;
  } else if (incQ.data && orgQ.data) {
    const d = incQ.data;
    const org = orgQ.data.items.find((o) => o.id === d.organization_id);
    const rateById = new Map((vatRatesQ.data ?? []).map((r) => [r.id, r]));
    const stavkaOf = (rid: string) => {
      const r = rateById.get(rid);
      if (!r) return '';
      const pct = Number(r.rate_percent);
      if (pct > 0) return String(pct);
      return r.code === 'VAT_0' ? '902' : '903';
    };
    const dExt = d as typeof d & {
      corrected_number?: string | null; corrected_date?: string | null;
    };
    const header = {
      is_correction: !!d.is_correction,
      invoice_number: d.invoice_number,
      invoice_date: d.invoice_date,
      not_issued: false,
      not_issued_reason: null,
      seller: { name: d.contractor_name, ipn: d.contractor_inn ?? '',
        tin: d.contractor_edrpou ?? '' },
      buyer: { name: org?.full_name || org?.name || '',
        ipn: org?.vat_number ?? '', tin: org?.edrpou ?? '' },
      director: { name: null, inn: '' },
      corrected: d.is_correction && dExt.corrected_number
        ? { invoice_number: dExt.corrected_number,
            invoice_date: dExt.corrected_date ?? d.invoice_date }
        : null,
    };
    // 2026-08-07 (зауваження власника): вхідний РК друкується БЛАНКОМ РК
    // (J1201216), не бланком ПН. Групи/причини (гр.1.1, 2.1) XML-виписка
    // у нас не зберігає — колонки лишаються порожніми; суми — дельти.
    data = d.is_correction ? {
      kind: 'RK',
      header,
      rows: d.items.map((it): RkRow => ({
        g001: '', g21: '', g22: '', g3s: it.description,
        g4: it.ukt_zed_code, g4s: null, g105: null, g008: '',
        g5: it.quantity, g6: it.price_without_vat,
        g010: it.base_amount, g11: it.vat_amount,
      })),
    } : {
      kind: 'PN',
      header,
      rows: d.items.map((it, i): PnRow => ({
        n: i + 1, descr: it.description, ukt: it.ukt_zed_code,
        uom_sign: it.uom_code, uom_code: null,
        qty: it.quantity, price: it.price_without_vat,
        stavka: stavkaOf(it.vat_rate_id),
        base: it.base_amount, vat: it.vat_amount,
      })),
    };
  }

  if (!data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const h = data.header;
  const isRk = data.kind === 'RK';

  // Розділ А ПН.
  const inc = kind === 'incoming' ? incQ.data : null;
  const pnA: [string, string, string | null][] | null = !isRk ? (() => {
    const src = kind === 'incoming' && inc ? inc : null;
    const sum = (rows: PnRow[], f: (r: PnRow) => number, pred?: (r: PnRow) => boolean) =>
      rows.filter(pred ?? (() => true)).reduce((s, r) => s + f(r), 0);
    const rows = data!.rows as PnRow[];
    const base20 = src ? Number(src.base_20) : sum(rows, (r) => Number(r.base), (r) => r.stavka === '20');
    const vat20 = src ? Number(src.vat_20) : sum(rows, (r) => Number(r.vat), (r) => r.stavka === '20');
    const base7 = src ? Number(src.base_7) : sum(rows, (r) => Number(r.base), (r) => r.stavka === '7');
    const vat7 = src ? Number(src.vat_7) : sum(rows, (r) => Number(r.vat), (r) => r.stavka === '7');
    const base14 = src ? Number(src.base_14) : sum(rows, (r) => Number(r.base), (r) => r.stavka === '14');
    const vat14 = src ? Number(src.vat_14) : sum(rows, (r) => Number(r.vat), (r) => r.stavka === '14');
    const base901 = sum(rows, (r) => Number(r.base), (r) => r.stavka === '901');
    const base902 = src ? Number(src.base_0) : sum(rows, (r) => Number(r.base), (r) => r.stavka === '902');
    const base903 = src ? Number(src.base_exempt) : sum(rows, (r) => Number(r.base), (r) => r.stavka === '903');
    const vatTotal = vat20 + vat7 + vat14;
    const total = base20 + base7 + base14 + base901 + base902 + base903 + vatTotal;
    const f = (n: number) => n ? fmt2(n) : '-';
    return [
      ['І', 'Загальна сума коштів, що підлягають сплаті з урахуванням податку на додану вартість', fmt2(total)],
      ['ІI', 'Загальна сума податку на додану вартість, у тому числі:', f(vatTotal)],
      ['IIІ', 'загальна сума податку на додану вартість за основною ставкою', f(vat20)],
      ['ІV', 'загальна сума податку на додану вартість за ставкою 7%', f(vat7)],
      ['V', 'загальна сума податку на додану вартість за ставкою 14 %', f(vat14)],
      ['VІ', 'Усього обсяги постачання за основною ставкою (код ставки 20)', f(base20)],
      ['VІІ', 'Усього обсяги постачання за ставкою 7% (код ставки 7)', f(base7)],
      ['VІІІ', 'Усього обсяги постачання за ставкою 14 % (код ставки 14)', f(base14)],
      ['IX', 'Усього обсяги постачання при експорті товарів за ставкою 0% (код ставки 901)', f(base901)],
      ['X', 'Усього обсяги постачання на митній території України за ставкою 0% (код ставки 902)', f(base902)],
      ['XI', 'Усього обсяги операцій, звільнених від оподаткування (код ставки 903)', f(base903)],
      ['XII', 'Дані щодо зворотної (заставної) тари', '-'],
    ];
  })() : null;

  const t = data.totals ?? {};
  const fT = (v?: string) => v && Number(v) !== 0 ? fmt2(v) : '-';
  const rkA: [string, string, string][] | null = isRk ? [
    ['І', "Сума коригування податкового зобов'язання та податкового кредиту (-) (+), у тому числі:", fT(t.vat_total)],
    ['ІI', "сума коригування податкового зобов'язання та податкового кредиту за основною ставкою (-) (+)", fT(t.vat_20)],
    ['IIІ', "сума коригування податкового зобов'язання та податкового кредиту за ставкою 7% (-) (+)", fT(t.vat_7)],
    ['ІV', "сума коригування податкового зобов'язання та податкового кредиту за ставкою 14 % (-) (+)", fT(t.vat_14)],
    ['V', 'Усього підлягають коригуванню обсяги постачання без урахування податку на додану вартість, що оподатковуються за основною ставкою (-) (+) (код ставки 20)', fT(t.base_20)],
    ['VI', 'Усього підлягають коригуванню обсяги постачання без урахування податку на додану вартість, що оподатковуються за ставкою 7% (-) (+) (код ставки 7)', fT(t.base_7)],
    ['VII', 'Усього підлягають коригуванню обсяги постачання без урахування податку на додану вартість, що оподатковуються за ставкою 14 % (-) (+) (код ставки 14)', fT(t.base_14)],
    ['VІII', 'Усього підлягають коригуванню обсяги постачання без урахування податку на додану вартість, що оподатковуються за ставкою 0% (-) (+) (код ставки 901)', '-'],
    ['IX', 'Усього підлягають коригуванню обсяги постачання без урахування податку на додану вартість, що оподатковуються за ставкою 0% (-) (+) (код ставки 902)', fT(t.base_902)],
    ['X', 'Усього підлягають коригуванню обсяги операцій, звільнених від оподаткування (-) (+) (код ставки 903)', fT(t.base_903)],
  ] : null;

  return (
    <div className="print-page invoice-print" style={{
      maxWidth: 1100, margin: '0 auto', padding: '1rem',
      fontFamily: "'Times New Roman', serif", fontSize: '8.5pt',
      background: 'var(--bg-panel)', color: '#000',
    }}>
      <style>{`
        .pnb-table { width: 100%; border-collapse: collapse; font-size: 7.5pt; line-height: 1.15; }
        .pnb-table th, .pnb-table td { border: 1px solid #000; padding: 1px 3px; }
        .pnb-table th { text-align: center; font-weight: 400; font-size: 6.5pt; text-transform: none; letter-spacing: 0; background: #fff; color: #000; vertical-align: middle; }
        @media print { @page { size: A4 landscape; margin: 1cm; } }
      `}</style>

      <div className="actions print-hide" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button onClick={() => window.print()} className="primary">друкувати</button>
        <button onClick={() => window.history.back()}>назад</button>
        <SaveXlsxButton />
      </div>

      {/* Шапка: відмітки ліворуч, гриф праворуч. */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '46%', verticalAlign: 'top' }}>
              <TopMarks h={h} />
              <ExportInfo />
            </td>
            <td style={{ width: '8%' }}></td>
            <td style={{ verticalAlign: 'top', fontSize: '8.5pt' }}>
              {isRk ? (
                <div style={{ textAlign: 'left', paddingTop: 10 }}>Додаток 2<br />до податкової накладної</div>
              ) : (
                <div>
                  ЗАТВЕРДЖЕНО<br />
                  Наказ Міністерства фінансів України<br />
                  31 грудня 2015 року № 1307 (у редакції наказу<br />
                  Міністерства фінансів України<br />
                  від 09 серпня 2024 року № 400)
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Титул + номер/дата. */}
      <div style={{ textAlign: 'center', margin: '10px 0 4px' }}>
        {isRk ? (
          <>
            <b>РОЗРАХУНОК КОРИГУВАННЯ КІЛЬКІСНИХ І ВАРТІСНИХ ПОКАЗНИКІВ</b>{' '}
            <Box v={dmy(h.invoice_date)} w={80} /> <Box v={h.invoice_number} w={70} /> / <Box w={40} />
            <div style={{ marginTop: 4 }}>
              <b>ДО ПОДАТКОВОЇ НАКЛАДНОЇ</b> від{' '}
              <Box v={h.corrected ? dmy(h.corrected.invoice_date) : ''} w={80} /> №{' '}
              <Box v={h.corrected?.invoice_number ?? ''} w={70} /> / <Box w={40} />
            </div>
            <div style={sub}>(дата складання) (порядковий номер)</div>
          </>
        ) : (
          <>
            <b style={{ fontSize: '10pt' }}>ПОДАТКОВА НАКЛАДНА</b>{' '}
            <Box v={dmy(h.invoice_date)} w={80} /> <Box v={h.invoice_number} w={70} /> / <Box w={40} />
            <div style={sub}>(дата складання) (порядковий номер) (¹)</div>
          </>
        )}
      </div>

      {/* Сторони. */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
        <tbody>
          <tr>
            <td style={{ width: '49%', verticalAlign: 'top' }}>
              <Party title="Постачальник (продавець)" p={h.seller} />
            </td>
            <td style={{ width: '2%' }}></td>
            <td style={{ verticalAlign: 'top' }}>
              <Party title="Отримувач (покупець)" p={h.buyer} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Розділ А. */}
      <div style={{ fontWeight: 700, margin: '6px 0 2px' }}>Розділ А</div>
      <table className="pnb-table">
        <tbody>
          {(isRk ? rkA! : pnA!).map(([num, label, val]) => (
            <tr key={num}>
              <td style={{ width: 30, textAlign: 'center', fontWeight: 700 }}>{num}</td>
              <td style={{ fontWeight: num.length <= 2 ? 700 : 400 }}>{label}</td>
              <td style={{ width: 110, textAlign: 'right', fontWeight: 700 }}>{val}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Розділ Б. */}
      <div style={{ fontWeight: 700, margin: '6px 0 2px' }}>Розділ Б</div>
      {isRk ? (
        <table className="pnb-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: 24 }}>№ з/п</th>
              <th colSpan={2}>Причина коригування</th>
              <th rowSpan={2} style={{ width: 28 }}>№ з/п групи коригування</th>
              <th rowSpan={2}>Опис (номенклатура) товарів/послуг, вартість чи
                кількість яких коригується</th>
              <th colSpan={4}>Код</th>
              <th colSpan={2}>Одиниця виміру товару/послуги</th>
              <th colSpan={2}>Коригування кількості</th>
              <th colSpan={2}>Коригування вартості</th>
              <th rowSpan={2} style={{ width: 30 }}>Код ставки</th>
              <th rowSpan={2} style={{ width: 30 }}>Код пільги⁸</th>
              <th rowSpan={2} style={{ width: 74 }}>Обсяги постачання (база
                оподаткування) без урахування податку на додану вартість</th>
              <th rowSpan={2} style={{ width: 66 }}>Сума податку на додану
                вартість</th>
            </tr>
            <tr>
              <th style={{ width: 44 }}>№ з/п рядка податкової накладної, що
                коригується або додається</th>
              <th style={{ width: 34 }}>код причини</th>
              <th style={{ width: 60 }}>товару згідно з УКТ ЗЕД</th>
              <th style={{ width: 28 }}>імпорт⁶</th>
              <th style={{ width: 40 }}>власна сільськогосподарська продукція⁷</th>
              <th style={{ width: 40 }}>послуги згідно з ДКПП</th>
              <th style={{ width: 40 }}>умовне позначення (українське)</th>
              <th style={{ width: 30 }}>код</th>
              <th style={{ width: 56 }}>кількість, об'єм, обсяг (-) (+)</th>
              <th style={{ width: 56 }}>ціна постачання товарів/послуг або
                максимальна роздрібна ціна товарів</th>
              <th style={{ width: 52 }}>ціна або максимальна роздрібна ціна (-) (+)</th>
              <th style={{ width: 52 }}>кількість постачання товарів/послуг</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontSize: '6pt', textAlign: 'center' }}>
              {['1.1', '1.2', '2.1', '2.2', '3', '4.1', '4.2.1', '4.2.2', '4.3',
                '5', '6', '7', '8', '9', '10', '11', '12', '13', '14']
                .map((n, i) => <td key={i}>{n}</td>)}
            </tr>
            {(data.rows as RkRow[]).map((r, i) => (
              <tr key={i}>
                <td style={cellC}>{i + 1}</td>
                <td style={cellC}>{r.g001}</td>
                <td style={cellC}>{r.g21}</td>
                <td style={cellC}>{r.g22}</td>
                <td style={cell}>{r.g3s}</td>
                <td style={cellC}>{r.g4 || '-'}</td>
                <td style={cellC}>-</td>
                <td style={cellC}>-</td>
                <td style={cellC}>-</td>
                <td style={cellC}>{r.g4s || '-'}</td>
                <td style={cellC}>{r.g105 || '-'}</td>
                <td style={cellR}>{fmtQ(r.g5)}</td>
                <td style={cellR}>{fmtQ(r.g6)}</td>
                <td style={cellR}>-</td>
                <td style={cellR}>-</td>
                <td style={cellC}>{r.g008}</td>
                <td style={cellC}>-</td>
                <td style={cellR}>{fmt2(r.g010)}</td>
                <td style={cellR}>{fmt2(r.g11)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="pnb-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: 26 }}>№ з/п</th>
              <th rowSpan={2}>Опис (номенклатура) товарів/послуг продавця</th>
              <th colSpan={4}>Код</th>
              <th colSpan={2}>Одиниця виміру товару/послуги</th>
              <th rowSpan={2} style={{ width: 70 }}>Кількість (об'єм, обсяг)</th>
              <th rowSpan={2} style={{ width: 84 }}>Ціна постачання одиниці
                товару/послуги без урахування ПДВ</th>
              <th rowSpan={2} style={{ width: 36 }}>Код ставки</th>
              <th rowSpan={2} style={{ width: 36 }}>Код пільги⁸</th>
              <th rowSpan={2} style={{ width: 84 }}>Обсяги постачання (база
                оподаткування) без урахування ПДВ</th>
              <th rowSpan={2} style={{ width: 74 }}>Сума податку на додану
                вартість</th>
            </tr>
            <tr>
              <th style={{ width: 66 }}>товару згідно з УКТ ЗЕД</th>
              <th style={{ width: 34 }}>імпорт⁶</th>
              <th style={{ width: 44 }}>власна сільськогосподарська продукція⁷</th>
              <th style={{ width: 46 }}>послуги згідно з ДКПП</th>
              <th style={{ width: 44 }}>умовне позначення (українське)</th>
              <th style={{ width: 34 }}>код</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontSize: '6pt', textAlign: 'center' }}>
              {['1', '2', '3.1', '3.2.1', '3.2.2', '3.3', '4', '5', '6', '7', '8', '9', '10', '11']
                .map((n, i) => <td key={i}>{n}</td>)}
            </tr>
            {(data.rows as PnRow[]).map((r) => (
              <tr key={r.n}>
                <td style={cellC}>{r.n}</td>
                <td style={cell}>{r.descr}</td>
                <td style={cellC}>{r.ukt || '-'}</td>
                <td style={cellC}>{r.ukt ? 'x' : '-'}</td>
                <td style={cellC}>-</td>
                <td style={cellC}>-</td>
                <td style={cellC}>{r.uom_sign || '-'}</td>
                <td style={cellC}>{r.uom_code || '-'}</td>
                <td style={cellR}>{fmtQ(r.qty)}</td>
                <td style={cellR}>{fmtQ(r.price)}</td>
                <td style={cellC}>{r.stavka}</td>
                <td style={cellC}>-</td>
                <td style={cellR}>{fmt2(r.base)}</td>
                <td style={cellR}>{fmt2(r.vat)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Підпис. */}
      <div style={{ marginTop: 10, fontSize: '8.5pt' }}>
        {isRk
          ? "Суми податку на додану вартість, які скориговані у зв'язку зі зміною кількісних чи вартісних показників, що зазначені в цьому розрахунку, визначені правильно та включені до податкового зобов'язання."
          : "Суми податку на додану вартість, нараховані (сплачені) у зв'язку з постачанням товарів/послуг, зазначених у цій накладній, визначені правильно, відповідають сумі податкових зобов'язань продавця."}
      </div>
      <table style={{ width: '100%', marginTop: 8, fontSize: '8.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: 260 }}>
              Посадова (уповноважена) особа/фізична особа<br />(законний представник)
            </td>
            <td style={{ borderBottom: B, textAlign: 'center', fontWeight: 700,
              width: 260 }}>
              {h.director.name || ' '}
            </td>
            <td style={{ width: 30 }}></td>
            <td style={{ border: B, textAlign: 'center', fontWeight: 700,
              width: 160 }}>
              {h.director.inn || ' '}
            </td>
          </tr>
          <tr style={{ fontSize: '6pt', color: '#333' }}>
            <td></td>
            <td style={{ textAlign: 'center' }}>(Власне ім'я ПРІЗВИЩЕ (за наявності))</td>
            <td></td>
            <td style={{ textAlign: 'center' }}>
              (реєстраційний номер облікової картки платника податків або серія
              (за наявності) та номер паспорта)
            </td>
          </tr>
        </tbody>
      </table>

      <div className="print-hide" style={{ marginTop: 12, color: 'var(--text-secondary)',
        fontSize: '8pt' }}>
        Чернетка для перевірки — юридичну силу має {isRk ? 'РК' : 'ПН'},
        зареєстрований{isRk ? '' : 'а'} в ЄРПН.
      </div>
    </div>
  );
}
